import { Injectable, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { AppRoutingService } from '@core/app-routing.service';
import { AppConfigService } from '@core/appconfig.service';
import { ProductService } from '@core/product.service';
import { AccessControlService } from '@core/services/access-control/access-control.service';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { UserService } from '@core/user.service';
import { EceProductService } from '@ece/api-services/ece-product.service';
import { ClassesBySchool } from '@models/classes-by-school';
import { Classroom } from '@models/classroom';
import { IClassroomProductDescription } from '@models/classroom-products';
import { SchoolLicense } from '@models/license/school-license';
import { NavigationMenuItem } from '@models/navigation-menu-item';
import { IProductLine } from '@models/product-line';
import { IProductLineVariant } from '@models/product-variant';
import { School } from '@models/school';
import { IUser } from '@models/user';
import { TranslateService } from '@ngx-translate/core';
import { CapabilitiesHelpers } from '@shared/capabilities-helpers';
import { EducationalUnitService } from '@shared/educational-unit/educational-unit.service';
import { Capabilities } from '@shared/enums/capabilities';
import { CapabilityType } from '@shared/enums/capability-type';
import { CompanyCode } from '@shared/enums/company-code';
import { LicenseFilterType } from '@shared/enums/license-filter-type';
import { LicenseAssignmentType } from '@shared/enums/license-status';
import { ProductType } from '@shared/enums/product-type';
import { RoleGroup, RoleType } from '@shared/enums/role-type';
import { TeacherLicenseAccess } from '@shared/enums/teacher-license-acccess';
import { VariantType } from '@shared/enums/variant-type';
import {
  buildParentsSubnav,
} from '@shared/header/branded-header/subnavigation/subnav-menu-builders/parents-subnavigation-menu.builder';
import { Helpers } from '@shared/helpers';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  tap
} from 'rxjs';
import { distinctUntilChanged, mergeMap } from 'rxjs/operators';
import { SchoolsFacade } from 'src/app/schools/store/schools.facade';
import { TeacherService } from 'src/app/teachers/teachers-service';
import { LearningFacade } from '../../classrooms/store/learning.facade';
import {
  buildDistrictAdminZBRedesignManageDropdownMenus,
  districtAdminManageDropdownMenusHighlightsPortal,
  districtHLAdminMainMenus,
  districtZBAdminMainMenus,
} from './branded-header/primary-menu-builders/district-admin-menu';
import { buildParentMenuItems } from './branded-header/primary-menu-builders/parent-menu';
import {
  buildSchoolAdminHLRedesignMenus,
  buildSchoolAdminZBRedesignMenus,
  schoolHLAdminMainMenus,
  schoolZBAdminMainMenus,
} from './branded-header/primary-menu-builders/school-admin-menu';
import { buildTeacherZBRedesignMenus, teacherHLMainMenus } from './branded-header/primary-menu-builders/teacher-menu';
import {
  buildClassesSubnav,
  ClassesSubNavMenuId
} from './branded-header/subnavigation/subnav-menu-builders/classes-subnavigation-menu-builder';
import {
  buildProductsSubnav
} from './branded-header/subnavigation/subnav-menu-builders/products-subnavigation-menu-builder';
import {
  buildReportsSubnav
} from './branded-header/subnavigation/subnav-menu-builders/reports-subnavigation-menu-builder';

/**
 * Used to build out the menus and dropdowns for the BrandedHeaderComponent used by HLPortal
 * Responsible for any API calls required to satiate menu items.
 * Can be used for the header changes needed for the future ZBPortal Redesign
 *
 * Menu building should be done here.
 *
 * ---
 * This should be as contextual as possible and have little reliance on other areas of the app driving it,
 * however there are times in the app when properties are set directly:

 * Flag to display more advanced menu options set by TeacherComponent & ProductComponent (both inside ProductModule).
 * When inside TeachersModule, classroom management pages use a resolver to update the currentClassroomId$.
 */
@Injectable({
  providedIn: 'root',
})
export class HeaderService implements OnDestroy, StatefulService {
  /**
   * Current Teacher License Products for when the Products dropdown primary menu item is displayed
   */
  currentMenuTeacherProducts: IProductLine[] = null;

  /**
   * Product lines available to user, including Class and Teacher products.
   */
  productLines: IProductLine[] = [];
  /**
   * Current menu items when the Manage dropdown primary menu item is displayed
   */
  adminManageDropdownMenuItems = [];
  /**
   * Current Primary Menu items for the navigation
   */
  currentMenus: NavigationMenuItem[] = [];
  /**
   * Assigned by modules directly when module adds header to the view
   */
  logoClickedFunction: Function = null;
  /**
   * Not all views have the Primary Menu Items shown (e.g. Choose Role page)
   */
  displayPrimaryHeaderMenus: boolean;
  brand: CompanyCode = null;
  isHighlightsPortalUser: boolean = false;

  /**
   * Current role that user is viewing the portal in.
   * Controls the navigation experience and will render different menus based on Role.
   */
  role$: BehaviorSubject<RoleType> = new BehaviorSubject<RoleType>(null);

  districtId: string = null;
  schoolId: string = null;
  userHasAdminBenchmarkCapabilities = false;
  hasHistoricalBenchmarkReports = signal(false);

  /** Maps schoolId to whether or not the school has admin benchmark report capabilities */
  readonly schoolAdminBenchmarkCapabilitiesMap: Record<string, boolean> = {};
  readonly hasRosterIntegrations$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hasClassroomLicenses = false;
  hasProgressReports = false;

  /**
   * Will trigger a rebuild of all primary header menu items when a true value is provided.
   * Primary menu items are those on the left side of the red bar portion of the header (next to brand logo).
   *
   * Should be called after getting new data from APIs
   */
  refreshHeader$: Subject<boolean> = new Subject<boolean>();

  /**
   * Schools used in the Manage menu dropdown menu (this is the secondary flyout window)
   */
  schools = [];

  /**
   * Submenu items for the items used in the Manage Menu dropdown items (this is the secondary flyout window)
   */
  adminSubMenus = {};

  /**
   * Updates when teachersService.currentSchoolId$ receives a new value.
   *
   * Will reorder the Classes dropdown for Teachers based off which School the Teacher is viewing.
   */
  currentSchoolId: string = null;

  /**
   * If Teacher has no Teacher licenses assigned, show the self-assign link in the products dropdown.
   *
   */
  showProductAssignLicensesButton: boolean = false;


  // Parent-specific Properties

  /**
   * The student currently in context for the parent portal.
   * Set by BrandedHeaderComponent when ParentFacade.studentContext$ emits.
   */
  parentStudentContext: IUser | null = null;

  /**
   * Children (student associations) of the current parent user.
   * Used by the header to populate the parent children dropdown.
   *
   * Children and classrooms are already alphabetical.
   */
  parentMenuChildren: IUser[];

  // End Parent-specific Properties

  /**
   * These reorder so that the last selected school is the school at the top of the list.
   */
  classesMenuItems$: BehaviorSubject<ClassesBySchool[]> = new BehaviorSubject([]);

  /** Will show or remove the subnavigation bar. */
  showSubnav = signal(true);

  /**
   * Menu items that will be used in the subnavigation bar.
   */
  subNavMenuItems$: BehaviorSubject<NavigationMenuItem[]> = new BehaviorSubject([]);

  /**
   * Teacher Component is where Superkids and other Products that use a more complex navigation
   * (e.g. Handwriting 2025, Kickstart).
   */
  buildAdvancedSubnav$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Current classroom that is being managed. Used to build classes subnavigation.
   *
   */
  currentClassroomId$: BehaviorSubject<string> = new BehaviorSubject(null);

  /**
   * Rebuilds the Classes subnav bar with current Classroom being managed.
   *
   * Classroom context is loaded in TeachersRoutingModule when accessing any class-specific route.
   */
  buildClassesSubnavForClass$: Observable<void> = this.learningFacade.classroomContext$.pipe(
    tap((res) => {
      if (res === null) {
        this.subNavMenuItems$.next([]);
      } else {
        // If not previously in Classes, clear out old subnav items while classroom data is loading
        // Prevents 'lesson page' link from showing while coming from Teacher Products into Classrooms
        const previousWasClassesSubnav = this.subNavMenuItems$.value
          ?.some(menu => menu.id === ClassesSubNavMenuId.Overview);

        if (!previousWasClassesSubnav) {
          this.subNavMenuItems$.next([]);
        }
      }
      return res;
    }),
    filter(classroom => !!classroom && !!classroom.students && !!classroom.licenses && !!classroom.classroom), // initializes from null to empty arrays
    map((classroom) => {
      buildClassesSubnav(
        this.appRoutingService,
        this.subNavMenuItems$,
        classroom
      );
    })
  );

  private moduleRoutesByRoleType = {
    [RoleType.DistrictAdministrator]: `..${this.appRoutingService.getDistrictsModuleRoute()}`,
    [RoleType.SchoolAdministrator]: `..${this.appRoutingService.getSchoolsModuleRoute()}`,
  };

  private getRouteIdToUseByRoleType = {
    [RoleType.DistrictAdministrator]: () => this.districtId,
    [RoleType.SchoolAdministrator]: schoolId => schoolId || this.schoolId,
  };

  set headerProducts(val: IProductLine[]) {
    this.currentMenuTeacherProducts = val;
  }

  get headerProducts() {
    return this.currentMenuTeacherProducts;
  }

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private productService: ProductService,
    private appConfig: AppConfigService,
    private userService: UserService,
    private eceProductService: EceProductService,
    private router: Router,
    private learningFacade: LearningFacade,
    private schoolsFacade: SchoolsFacade,
    private appRoutingService: AppRoutingService,
    private translateService: TranslateService,
    private teacherService: TeacherService,
    private accessControlService: AccessControlService,
    private educationUnitService: EducationalUnitService,
  ) {
    this.isHighlightsPortalUser = this.appConfig.loginBrand === CompanyCode.HighlightsPortal;
    this.subscriptions.add(this.buildAdvancedSubnav$?.subscribe());
    this.subscriptions.add(this.getLicenses$?.subscribe());

    userService.user$.pipe(
      filter(user => !!user?.profileDetail)
    ).subscribe((user) => {
      if (!user.schools || user.schools?.length === 0) {
        this.districtId = user.districts[0]?.districtId;
      } else {
        this.districtId = user.schools[0]?.districtId;
        this.schoolId = user.schools[0]?.schoolId;
        this.schools = user.schools;
      }
      this.role$.next(user.viewingAsRole ?? user.roles?.[0]);
    });

    let buildClassesSubnavSubscription: Subscription;
    // Subscribe to router events and user role first, this will trigger the menu to be built
    combineLatest([this.refreshHeader$, router.events ?? EMPTY]).pipe(
      filter(([, event]) => event instanceof NavigationEnd),
      map(([, event]) => event as RouterEvent),
    ).subscribe((routerEvent: RouterEvent) => {
      this.isHighlightsPortalUser = this.appConfig.loginBrand === CompanyCode.HighlightsPortal;
      const roleHasSubnav = this.userService.user?.viewingAsRole === RoleType.Teacher
        || this.userService.user?.viewingAsRole === RoleType.Parent;

      if (roleHasSubnav) {
        const showSubnav = (this.isClassesRoute(routerEvent.url)
            || this.isProductsRoute(routerEvent.url)
            || this.isTeacherReportsRoute(routerEvent.url))
            || this.isParentRoute(routerEvent.url);
        if (showSubnav) {
          if (buildClassesSubnavSubscription) {
            buildClassesSubnavSubscription.unsubscribe();
            buildClassesSubnavSubscription = null;
          }
          if (this.isClassesRoute(routerEvent.url)) {
            buildClassesSubnavSubscription = this.buildClassesSubnavForClass$.subscribe();
          } else if (this.isProductsRoute(routerEvent.url)) {
            let showFiveDayPlanner: boolean = false;
            let showLessonPage: boolean = false;

            // Sets up values to build out the menu used for the Teachers component (e.g. Superkids, Handwriting 2025)
            if (this.buildAdvancedSubnav$?.value) {
              const productLine = this.productService.getProductLineByKey(
                this.productService.currentProductLines$?.value,
                this.productService.currentProductLineKey$.value
              );
              const variant = this.productService.getProductVariantByVariantType(
                productLine,
                this.productService.currentVariantType$?.value
              );

              if (variant) {
                showFiveDayPlanner = Helpers.hasProductVariantCapability(
                  variant, Capabilities.FiveDayPlanner, CapabilityType.Teacher);
                showLessonPage = Helpers.hasProductVariantCapability(
                  variant, Capabilities.LessonPage, CapabilityType.Teacher);
              }
            }

            buildProductsSubnav(
              this.appRoutingService,
              this.buildAdvancedSubnav$?.value,
              this.subNavMenuItems$,
              this.productService,
              this.isHighlightsPortalUser,
              showFiveDayPlanner,
              showLessonPage
            );
          } else if (this.isTeacherReportsRoute(routerEvent.url)) {
            const studentActivityReportActive = routerEvent.url.includes('student-activity');
            buildReportsSubnav(
              this.appRoutingService,
              this.subNavMenuItems$,
              this.hasProgressReports,
              studentActivityReportActive
            );
          }
          // This should be called last after all menu configurations are complete.
          this.showSubnav.set(true);
        } else {
          this.showSubnav.set(false);
        }
      } else {
        this.showSubnav.set(false);
      }
    });


    this.role$.pipe(
      distinctUntilChanged(),
      filter(role => !!role && RoleGroup.digitalAdmins.includes(role)),
      mergeMap(() => this.educationUnitService.getEducationalUnit())
    ).subscribe((res) => {
      if (res.success) {
        this.hasRosterIntegrations$.next(res?.response.some(edUnit => edUnit.isOneRoster));
      }
    });
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    // Reset product and menu state
    this.currentMenuTeacherProducts = null;
    this.productLines = [];
    this.adminManageDropdownMenuItems = [];
    this.currentMenus = [];
    this.logoClickedFunction = null;
    this.displayPrimaryHeaderMenus = undefined;
    this.brand = null;
    this.isHighlightsPortalUser = false;

    // Reset BehaviorSubjects to initial values
    this.role$.next(null);
    this.hasRosterIntegrations$.next(false);
    this.refreshHeader$.next(false);
    this.classesMenuItems$.next([]);
    this.showSubnav.set(true);
    this.subNavMenuItems$.next([]);
    this.buildAdvancedSubnav$.next(false);
    this.currentClassroomId$.next(null);

    // Reset IDs, capabilities, and flags
    this.districtId = null;
    this.schoolId = null;
    this.userHasAdminBenchmarkCapabilities = false;
    this.hasClassroomLicenses = false;
    this.hasProgressReports = false;
    this.currentSchoolId = null;
    this.showProductAssignLicensesButton = false;
    this.parentStudentContext = null;

    // Reset collections
    this.schools = [];
    this.adminSubMenus = {};
    this.parentMenuChildren = [];

    // Reset Signals
    this.hasHistoricalBenchmarkReports.set(false);

    // Clear the capabilities map
    Object.keys(this.schoolAdminBenchmarkCapabilitiesMap).forEach((key) => {
      delete this.schoolAdminBenchmarkCapabilitiesMap[key];
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Navigates to item selected and closes the dropdown menu that was clicked in.
   *
   * @param event Event to cancel default
   * @param primaryMenu Menu item being selected
   */
  navigateToManageMenuDropdownItem(event: Event, primaryMenu: NavigationMenuItem) {
    event.preventDefault();
    const moduleRoute = this.moduleRoutesByRoleType[primaryMenu.roleType];
    const routeId = this.getRouteIdToUseByRoleType[primaryMenu.roleType];

    if (moduleRoute && routeId) {
      this.router.navigate([moduleRoute, routeId(primaryMenu.schoolId), primaryMenu.route]);
    }
  }

  /**
   * Initiates router navigation for a given product line or variant.
   */
  navigateToProduct(product: IProductLine, variant: IProductLineVariant, isTeacher: boolean = true) {
    const hlProductLineKey = product.productLineKey;
    const hlVariantType: VariantType = variant.variantType;
    const { themeId } = this.eceProductService;
    const hlProductLineKeyLower = product?.productLineKey?.toLowerCase();

    if (hlProductLineKeyLower && ProductType[hlProductLineKeyLower] === ProductType.ece2024) {
      this.eceNavigation(hlProductLineKey, hlVariantType, themeId);
    } else {
      // Use the standard way of routing, which does not need query parameters.
      // Clear out queryParams in case we are switching products
      this.router.navigate(
        this.appRoutingService
          .getProductVariantRoute(product, variant, isTeacher)
          .toArray(),
        { queryParams: {} }
      );
    }
  }

  navigateToStudentClassroom(studentUserId: string, classroomId: string) {
    this.router.navigate(
      this.appRoutingService
        .getParentsStudentClassroomRoute(studentUserId, classroomId)
        .toArray(),
      { queryParams: {} }
    );
  }

  private eceNavigation(productLineKey: string, variantType: VariantType, themeId: string) {
    this.router.navigate(
      this.appRoutingService.getECEWeeklyPlannerRoute().toArray(),
      {
        queryParams: {
          productLineKey,
          variantType,
          plannerThemeId: themeId,
        },
        replaceUrl: true
      });
  }

  private isClassesRoute = (url: string) => url
    .startsWith(`${this.appRoutingService.getTeachersClassesRoute()}/`);

  private isTeacherReportsRoute = (url: string) => url
    .startsWith(`${this.appRoutingService.getTeachersReportsRoute()}/`);

  private isProductsRoute = (url: string) => url
    .startsWith(`${this.appRoutingService.getTeachersProductModule()}/`);

  private isParentRoute = (url: string) => url
    .startsWith(`${this.appRoutingService.getParentModuleRoute()}/`);

  /**
   * Used by Teacher role.
   *
   * Gets the product lines in Learning Facade for Teachers.
   *
   * If user has no products, learningFacade returns an empty array.
   *
   * Subscribed to through the teacherHeaderResolver.
   *
   * Subscribing through the resolver ensures the LearningFacade's stores exist before
   * subscribing to them. If the store is not yet instantiated when this is subscribed to,
   * this will never be triggered because `learningFacade.classrooms$` is not yet assigned to a store.
   */
  getProducts$ = combineLatest([
    this.learningFacade.productLines$,
    this.role$
  ]).pipe(
    filter(([, role]) => role === RoleType.Teacher || role === RoleType.Parent),
    filter(([products]) => !!products),
    switchMap(([products]) => {
      this.currentMenuTeacherProducts = this.productService.getProductsWithTeacherLicenses(products);
      this.productLines = products;
      this.hasProgressReports = CapabilitiesHelpers.productLinesHaveCapability(
        this.productLines, Capabilities.ProgressReports, CapabilityType.Classroom);
      const hasProductsAssigned = this.accessControlService.teacherAssignedLicenseAccessCheck(products, null, null);
      this.showProductAssignLicensesButton = hasProductsAssigned
        === TeacherLicenseAccess.NeedsAvailableLicensesAssigned
        && this.userService.user.profileDetail.canManageLicenses;
      this.refreshHeader$.next(true);
      return of(null);
    }),
    shareReplay(), // shareReplay keeps the subscription alive once it was subscribed to in the teacher's header resolver
  );

  /**
   * Used by Teacher role.
   *
   * Subscribed to through the teacherHeaderResolver.
   *
   * Subscribing through the resolver ensures the LearningFacade's stores exist before
   * subscribing to them. If the store is not yet instantiated when this is subscribed to,
   * this will never be triggered because `learningFacade.classrooms$` is not yet assigned to a store.
   */
  getClassrooms$: Observable<[Classroom[], string]> = combineLatest([
    this.learningFacade.classrooms$,
    this.teacherService.currentSchoolId$,
    this.userService.user$,
  ]).pipe(
    // wait for the user to exist, since it is required for grouping the classrooms
    filter(([/* classrooms */, /* currentSchoolId */, user]) => !!user),
    tap(([classrooms, currentSchoolId]) => {
      this.currentSchoolId = currentSchoolId;
      const allClassrooms = classrooms ? [...classrooms.filter(c => c.roleType !== RoleType.Parent)] : [];

      // Sort schoolInfo array alphabetically by school name.
      allClassrooms.sort((a, b) => a?.name?.localeCompare(b.name));

      this.groupClassroomsBySchool(allClassrooms);
    }),
    // remove user from output
    map(([classrooms, currentSchoolId]): [Classroom[], string] => [classrooms, currentSchoolId]),
    shareReplay(), // shareReplay keeps the subscription alive once it was subscribed to in the teacher's header resolver
  );

  getLicenses$ = this.role$.pipe(
    distinctUntilChanged(),
    // this filter limits the license API calls to only users that need the data
    filter(role => RoleGroup.teachers.includes(role)
      || RoleGroup.digitalAdmins.includes(role)),
    switchMap((role) => {
      if (this.districtId && role === RoleType.DistrictAdministrator) {
        // District admins can avoid making multiple API calls
        return this.productService.getUserDistrictLicenses(this.districtId, {
          userId: this.userService.user.userId,
          licenseFilterType: LicenseFilterType.ShowAssigned,
        }).pipe(
          map((res) => {
            if (res.success) {
              return res.response;
            }
            throw new Error(res.messages[0]);
          })
        );
      }
      // Otherwise, we call a service method that makes 1 API call per school
      return this.schoolsFacade.schoolLicenses$.pipe(
        // do some logic that requires the school/license pair
        tap((schoolLicensePairs) => {
          schoolLicensePairs?.forEach((pair) => {
            this.schoolAdminBenchmarkCapabilitiesMap[pair.key] = CapabilitiesHelpers.schoolLicensesHaveCapability(
              pair.value, Capabilities.BenchmarkReports, CapabilityType.Admin
            );
          });
        }),
        // flatten the data into all license for the user
        map(schoolLicensePairs => schoolLicensePairs?.flatMap(
          pair => pair.value ?? [])
        )
      );
    }),
    filter((userLicenses: SchoolLicense[]) => !!userLicenses),
    // check all licenses available to the user
    tap((userLicenses: SchoolLicense[]) => {
      this.userHasAdminBenchmarkCapabilities = CapabilitiesHelpers.schoolLicensesHaveCapability(
        userLicenses, Capabilities.BenchmarkReports, CapabilityType.Admin
      );
      this.hasClassroomLicenses = userLicenses.filter(x => x.type === LicenseAssignmentType.Classroom).length > 0
        || CapabilitiesHelpers.schoolLicensesHaveCapability(
          userLicenses,
          Capabilities.SeatBasedLicense,
          CapabilityType.Classroom);
      this.hasProgressReports = userLicenses.filter(c => c.classroomCapabilities != null && c.classroomCapabilities
        .includes(Capabilities.ProgressReports)).length > 0;
      this.refreshHeader$.next(true);
    })
  );

  private groupClassroomsBySchool(classrooms: Classroom[]) {
    const grouped: any = classrooms.reduce((acc, classroom: Classroom) => {
      const { educationalUnitId } = classroom;
      if (!acc[educationalUnitId]) {
        acc[educationalUnitId] = [];
      }
      const matchingSchool = this.userService.user?.schools
        .find(school => school.educationalUnitId === classroom.educationalUnitId);

      const classToAdd = {
        ...classroom,
        schoolName: matchingSchool?.name,
      };

      acc[educationalUnitId].push(classToAdd);
      return acc;
    }, {} as ClassesBySchool);

    // Get array of schoolNames and their corresponding educationalUnitIds.
    const schoolInfo = Object.keys(grouped).map(schoolId => ({
      educationalUnitId: schoolId,
      schoolName: grouped[schoolId][0]?.schoolName
    }));

    // Sort schoolInfo array alphabetically by school name.
    schoolInfo.sort((a, b) => a?.schoolName?.localeCompare(b.schoolName));

    // Add entries from original group into new group object with alphabetical keys.
    let result = {};
    schoolInfo.forEach((info) => {
      result[info.educationalUnitId] = grouped[info.educationalUnitId];
    });

    // If there's a previously selected school ID, make that school be the first if it's a valid school for user
    if (this.currentSchoolId) {
      const schoolIdIsValid = this.userService.user.schools?.length > 0
        && this.userService.user.schools?.some((school: School) => school.educationalUnitId === this.currentSchoolId);

      if (schoolIdIsValid) {
        const schoolClassGroup = result[this.currentSchoolId];
        delete result[this.currentSchoolId];
        result = {
          [this.currentSchoolId]: schoolClassGroup,
          ...result,
        };
      }
    }

    const sortedClassesAsArray = Object.entries(result as ClassesBySchool);
    const classesMenuOptions: ClassesBySchool[] = [];

    // Return array of ordered classroom groups for better handling on UI.
    sortedClassesAsArray.forEach((entry) => {
      const schoolId = entry[0];
      const classGroupBySchool: Classroom[] = entry[1];
      const classIdToRouteMap: Record<string, string> = {};
      classGroupBySchool.forEach((c) => {
        classIdToRouteMap[c.classroomId] = this.appRoutingService.getTeachersClassroomRoute(c.classroomId).toString();
      });
      const newGroup: ClassesBySchool = {
        schoolId,
        schoolName: grouped[schoolId] ? grouped[schoolId][0]?.schoolName : null,
        classGroupBySchool,
        classIdToRouteMap,
      };
      classesMenuOptions.push(newGroup);
    });

    this.classesMenuItems$.next(classesMenuOptions);
  }

  private domainMatchesBrowserHistory(): boolean {
    const currentDomain = window.location.hostname;
    const previousState = window.history.state;
    const previousDomain = previousState ? previousState.previousDomain : null;

    if (!previousDomain) {
      return false;
    }

    return currentDomain === previousDomain;
  }

  /**
   * Gets drop down menus for a specific role depending on portal.
   *
   * This could be the 'Manage' menu for external Admins or 'Products' for Teacher
   */
  getDropdownMenu(role: RoleType): NavigationMenuItem[] {
    if (this.isHighlightsPortalUser) {
      if (role === RoleType.DistrictAdministrator) {
        return [...districtAdminManageDropdownMenusHighlightsPortal];
      }
      if (role === RoleType.SchoolAdministrator) {
        let menus = [];
        this.schools.forEach(((school) => {
          const needsSchoolId = !school?.abbreviatedSchoolId;
          const schoolId = school.educationalUnitId;
          menus = buildSchoolAdminHLRedesignMenus(needsSchoolId, schoolId);
          menus.sort((x, y) => x.weight - y.weight);

          this.adminSubMenus[school.educationalUnitId] = menus;
        }));

        return menus;
      }
    } else {
      if (role === RoleType.DistrictAdministrator) {
        const licenseExpired = !this.userService.hasLicenses;
        const menus = buildDistrictAdminZBRedesignManageDropdownMenus(licenseExpired);

        // Dynamically add any menu items that are conditionally displayed.
        menus.push({
          id: 'class-user-statistics',
          isRouted: false,
          isExternal: false,
          label: 'Class and user statistics',
          weight: 7,
          roleType: RoleType.DistrictAdministrator,
          onClickFunction: () => {
            this.router.navigate(this.appRoutingService.getDistrictClassAndUserStatisticsReportRoute(
              this.districtId).toArray(),
            {
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
          },
        });

        // TODO: 17239 - Add product specific filtering
        if (this.userHasAdminBenchmarkCapabilities || this.hasHistoricalBenchmarkReports()) {
          menus.push({
            id: 'benchmark-reports',
            isRouted: false,
            isExternal: false,
            label: 'Benchmark Reports',
            weight: 8,
            roleType: RoleType.DistrictAdministrator,
            onClickFunction: () => {
              // TODO add this to AppRoutingService
              this.router.navigate(['districts', this.districtId, 'reports', 'benchmark-reports'], {
                queryParams: {
                  districtId: this.districtId,
                  classroomId: null
                },
                queryParamsHandling: 'merge',
                replaceUrl: true
              });
            },
          });
        }

        if (this.hasRosterIntegrations$.value) {
          menus.push({
            id: 'integration-dashboard',
            isRouted: false,
            isExternal: false,
            label: 'Integrations Dashboard',
            weight: 9,
            roleType: RoleType.DistrictAdministrator,
            disabled: licenseExpired,
            onClickFunction: () => {
              // TODO add this to AppRoutingService
              this.router.navigate(['/districts', this.districtId, 'integrations'], {
                queryParamsHandling: 'merge',
                replaceUrl: true
              });
            },
          });
        }

        return menus;
      }
      if (role === RoleType.SchoolAdministrator) {
        let menus = [];
        this.schools.forEach(((school) => {
          const needsSchoolId = !school?.abbreviatedSchoolId;
          const schoolId = school.educationalUnitId;
          const licenseExpired = !this.userService.hasLicenses;
          menus = buildSchoolAdminZBRedesignMenus(needsSchoolId, schoolId, licenseExpired);

          menus.push({
            id: 'class-user-statistics',
            isRouted: false,
            isExternal: false,
            label: 'Class and user statistics',
            weight: 7,
            roleType: RoleType.SchoolAdministrator,
            onClickFunction: () => {
              this.router.navigate(
                this.appRoutingService.getSchoolClassAndUserStatisticsReport(school.educationalUnitId).toArray(),
                {
                  queryParamsHandling: 'merge',
                  replaceUrl: true
                });
            },
          });
          
          // TODO: 17239 - Add product specific filtering
          if (this.schoolAdminBenchmarkCapabilitiesMap[schoolId] || this.hasHistoricalBenchmarkReports()) {
            menus.push({
              id: 'benchmark-reports',
              isRouted: false,
              isExternal: false,
              label: 'Benchmark Reports',
              weight: 8,
              roleType: RoleType.SchoolAdministrator,
              onClickFunction: () => {
                this.router.navigate(['schools', this.schoolId, 'reports', 'benchmark-reports'], {
                  queryParams: {
                    districtId: this.districtId,
                    classroomId: null
                  },
                  queryParamsHandling: 'merge',
                  replaceUrl: true
                });
              },
            });
          }

          if (!this.districtId && this.hasRosterIntegrations$.value) {
            menus.push({
              id: 'integration-dashboard',
              isRouted: false,
              isExternal: false,
              label: 'Integrations Dashboard',
              weight: 9,
              roleType: RoleType.SchoolAdministrator,
              disabled: licenseExpired,
              onClickFunction: () => {
                this.router.navigate(['/schools', school.schoolId, 'integrations'], {
                  queryParamsHandling: 'merge',
                  replaceUrl: true
                });
              }
            });
          }

          menus.sort((x, y) => x.weight - y.weight);

          this.adminSubMenus[school.educationalUnitId] = menus;
        }));

        return menus;
      }
    }
    return [];
  }

  /**
   *  Builds the Products, Classes, and other Primary Menu items.
   *
   * Reports and Classes are dynamic based on licenses.
   * @returns Current Primary Menu items for the primary navigation header
   */
  buildTeacherPrimaryMenus(): NavigationMenuItem[] {
    let menus: NavigationMenuItem[] = [];

    // ZBPortal will check for Class licenses, HLPortal currently does not support Classes
    if (this.isHighlightsPortalUser) {
      menus = teacherHLMainMenus;

    } else {
      let showReports = false;
      if (this.productLines?.length) {
        showReports = Helpers.getHasClassroomProducts(this.productLines);
      }
      const showClasses = showReports
        || (this.userService.user.profileDetail.canManageClassroom && this.hasClassroomLicenses)
        || this.classesMenuItems$?.value?.length > 0;
      menus = buildTeacherZBRedesignMenus(this.appRoutingService, showReports, showClasses);
    }

    menus = Helpers.sortByWeight(menus);

    return menus;
  }

  /**
   * Builds the Primary Menu items for the parent portal.
   * @returns Current Primary Menu items for the primary navigation header
   */
  buildParentMenus(): NavigationMenuItem[] {
    const menus = buildParentMenuItems(this.parentMenuChildren, this.parentStudentContext, this.appRoutingService);

    return Helpers.sortByWeight(menus);
  }

  /**
   * Builds the Primary Menu items for the menu when viewing as a DistrictAdmin
   * @returns Current Primary Menu items for the primary navigation header
   */
  buildDistrictAdminMenus(): NavigationMenuItem[] {
    let menus: NavigationMenuItem[];

    if (this.isHighlightsPortalUser) {
      menus = districtHLAdminMainMenus;
    } else {
      menus = districtZBAdminMainMenus;
    }

    menus = Helpers.sortByWeight(menus);

    this.currentMenus = menus;
    return menus;
  }

  /**
   * Builds the Primary Menu items for the menu when viewing as a SchoolAdmin
   * @returns Current Primary Menu items for the
   */
  buildSchoolAdminMenus(): NavigationMenuItem[] {
    let menus: NavigationMenuItem[];
    if (this.isHighlightsPortalUser) {
      menus = schoolHLAdminMainMenus;
    } else {
      menus = schoolZBAdminMainMenus;
    }

    menus = Helpers.sortByWeight(menus);

    this.currentMenus = menus;
    return menus;
  }

  /**
   * Takes the user back to their last page.
   *
   * Will navigate to a referrer path when a referrer is provided.
   *
   * If the previous window history item is for the current domain, it will use that history item to go back to
   *
   * If none of the other options are true, then the user is navigated to their root route (e.g. Admin Overview pages)
   */
  goBack(): void {
    const { referrer } = document;

    if (referrer && (referrer !== window.location.toString())) {
      // use referrer first
      const referrerPath = new URL(referrer).pathname;

      this.router.navigateByUrl(referrerPath);
    } else if (window.history.length > 1 && this.domainMatchesBrowserHistory()) {
      // use history if exists for current domain
      window.history.back();
    } else {
      // go back to module root
      this.router.navigateByUrl('/');
    }
  }

  /**
   * Clears out the current user's viewingAsRole then navigates to the root which directs user to the Choose Role page.
   */
  chooseRoleClick() {
    this.userService.user.viewingAsRole = null;
    this.router.navigate(['/']);
  }

  buildParentSubnavigationMenu(classroom: Classroom, userId: string) {
    let showCurrentAssignmentsNavLink: boolean = true;

    const classroomProductDescriptions = classroom?.licenses
      .filter(item => item.productType === ProductType.fsk2021);

    if (classroomProductDescriptions.length > 0
      && classroom?.assignedLicenseCount === classroomProductDescriptions.length) {
      showCurrentAssignmentsNavLink = false;
    }

    const isSuperKidsClassroom = classroom?.licenses.some(
      (license: IClassroomProductDescription) => Helpers.superkidsLegacyProductTypes.includes(license.productType));

    const showStudentActivityReport = classroom.profile.enableParentPortalStudentScores;

    if (classroom?.assignedLicenseCount > 0) {
      buildParentsSubnav(
        this.appRoutingService,
        this.subNavMenuItems$,
        this.translateService,
        userId,
        classroom.classroomId,
        showCurrentAssignmentsNavLink,
        isSuperKidsClassroom,
        showStudentActivityReport);
    }
  }
}
